import { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, Linkedin, Instagram, Youtube, MessageCircle, ArrowRight, Mail, Phone, MapPin, Download } from "lucide-react";
import Logo from "../Logo";
import { useAuth } from "../../context/AuthContext";
import api from "../../lib/api";

const NAV = [
  { to: "/features", label: "Features" },
  { to: "/pricing", label: "Pricing" },
  { to: "/track-shipment", label: "Track Shipment" },
  { to: "/developers", label: "API" },
  { to: "/faq", label: "FAQ" },
  { to: "/contact", label: "Contact" },
];

const FOOTER_COLS = [
  { title: "Product", links: [["Shipping", "/domestic-shipping"], ["Courier Comparison", "/#rate-calculator"], ["Tracking", "/track"], ["COD", "/features#cod"], ["NDR", "/features#ndr"], ["Bulk Shipping", "/features#bulk"], ["International Shipping", "/international-shipping"]] },
  { title: "Company", links: [["About", "/about"], ["Contact", "/contact"], ["Pricing", "/pricing"], ["API", "/developers"], ["FAQ", "/faq"]] },
  { title: "Resources", links: [["Shipping Guide", "/domestic-shipping"], ["Tracking", "/track"], ["Help Center", "/faq"], ["Documentation", "/developers"]] },
  { title: "Legal", links: [["Terms & Conditions", "/terms"], ["Privacy Policy", "/privacy"], ["Refund Policy", "/refund-policy"], ["Shipping Policy", "/shipping-policy"], ["Prohibited Items", "/prohibited-items"]] },
];

export default function MarketingLayout({ children }) {
  const [open, setOpen] = useState(false);
  const { user } = useAuth();
  const [site, setSite] = useState(null);
  useEffect(() => {
    api.get("/public/site-settings").then(({ data }) => setSite(data)).catch(() => {});
  }, []);
  const contactEmail = site?.support_email || "shipanylogistics@gmail.com";
  const waDigits = (site?.whatsapp_number || "").replace(/\D/g, "");
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);
  const SOCIALS = [
    { icon: MessageCircle, label: "WhatsApp", href: site?.social_whatsapp || (waDigits ? `https://wa.me/${waDigits}` : "") },
    { icon: Instagram, label: "Instagram", href: site?.social_instagram || "" },
    { icon: Youtube, label: "YouTube", href: site?.social_youtube || "" },
    { icon: Linkedin, label: "LinkedIn", href: site?.social_linkedin || "" },
  ].filter((s) => s.href);
  const dashTo = user ? (user.role === "admin" ? "/admin" : "/app") : "/login";
  return (
    <div className="min-h-screen bg-shipany-navy text-white">
      <header className="sticky top-0 z-50 glass-navy border-b border-shipany-cyan/15">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-3.5">
          <Logo dark />
          <nav className="hidden items-center gap-7 lg:flex">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}-link`}
                className={({ isActive }) => `group relative py-1.5 text-sm font-medium transition-colors duration-300 ${isActive ? "text-shipany-cyan" : "text-slate-300 hover:text-white"}`}>
                {({ isActive }) => (
                  <>
                    {n.label}
                    <span className={`absolute -bottom-0.5 left-0 h-0.5 rounded-full bg-shipany-cyan transition-all duration-300 ${isActive ? "w-full" : "w-0 group-hover:w-full"}`} />
                  </>
                )}
              </NavLink>
            ))}
            <a href="/downloads/shipany-api-sandbox-kit.pdf" download data-testid="nav-sandbox-kit-download"
              className="group relative flex items-center gap-1.5 py-1.5 text-sm font-medium text-slate-300 transition-colors duration-300 hover:text-white">
              <Download className="h-3.5 w-3.5 text-[#FF9933]" /> Sandbox Kit
            </a>
          </nav>
          <div className="hidden items-center gap-3 lg:flex">
            {user ? (
              <Link to={dashTo} data-testid="nav-dashboard-btn" className="rounded-lg bg-shipany-blue px-5 py-2 text-sm font-semibold text-white transition-colors hover:bg-[#0648B3] glow-blue">Open Dashboard</Link>
            ) : (
              <>
                <Link to="/login" data-testid="nav-login-btn" className="text-sm font-medium text-slate-300 transition-colors hover:text-white">Login</Link>
                <Link to="/signup" data-testid="nav-signup-btn" className="rounded-lg bg-gradient-to-r from-[#FF9933] to-[#FF8000] px-5 py-2 text-sm font-bold text-[#071B3A] transition-all hover:shadow-[0_6px_24px_rgba(255,153,51,0.45)]">Sign Up Free</Link>
              </>
            )}
          </div>
          <button className="lg:hidden text-white" onClick={() => setOpen(!open)} data-testid="nav-mobile-menu-btn" aria-label="Menu">
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
        <AnimatePresence>
          {open && (
            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.25, ease: "easeInOut" }}
              className="overflow-hidden border-t border-white/10 lg:hidden" data-testid="nav-mobile-menu">
              <div className="flex flex-col gap-3 px-5 py-4">
                {NAV.map((n) => (
                  <Link key={n.to} to={n.to} data-testid={`nav-mobile-${n.label.toLowerCase().replace(/\s+/g, "-")}-link`} onClick={() => setOpen(false)} className="text-sm font-medium text-slate-200">{n.label}</Link>
                ))}
                <a href="/downloads/shipany-api-sandbox-kit.pdf" download data-testid="nav-mobile-sandbox-kit-link" onClick={() => setOpen(false)}
                  className="flex items-center gap-2 text-sm font-medium text-slate-200"><Download className="h-4 w-4 text-[#FF9933]" /> API Sandbox Kit (PDF)</a>
                {!user && <Link to="/login" data-testid="nav-mobile-login-link" onClick={() => setOpen(false)} className="text-sm font-medium text-slate-200">Login</Link>}
                <Link to={user ? dashTo : "/signup"} data-testid="nav-mobile-signup-btn" onClick={() => setOpen(false)} className="mt-2 rounded-lg bg-gradient-to-r from-[#FF9933] to-[#FF8000] px-5 py-2.5 text-center text-sm font-bold text-[#071B3A]">{user ? "Open Dashboard" : "Sign Up Free"}</Link>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      <main>{children}</main>

      <footer className="border-t border-white/10 bg-[#051430]" data-testid="marketing-footer">
        <div className="border-b border-white/10">
          <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-5 px-5 py-10 sm:flex-row">
            <div>
              <h3 className="font-display text-xl font-bold text-white sm:text-2xl" data-testid="footer-cta-title">Start Shipping with ShipAny</h3>
              <p className="mt-1 text-sm text-slate-400">Ship anywhere. Ship smarter — domestic, COD, bulk and international from one dashboard.</p>
            </div>
            <Link to="/signup" data-testid="footer-cta-btn"
              className="flex shrink-0 items-center gap-2 rounded-xl bg-gradient-to-r from-[#FF9933] to-[#FF8000] px-8 py-3.5 font-display text-sm font-bold text-[#071B3A] transition-all hover:shadow-[0_8px_30px_rgba(255,153,51,0.45)]">
              Sign Up Free <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
        <div className="mx-auto grid max-w-7xl gap-10 px-5 py-14 sm:grid-cols-2 lg:grid-cols-7">
          <div className="sm:col-span-2">
            <Logo dark />
            <p className="mt-4 font-display text-sm font-semibold text-saffron-green">SHIP ANYWHERE. SHIP SMARTER.</p>
            <p className="mt-2 max-w-xs text-sm leading-relaxed text-slate-400">Smart shipping infrastructure for modern businesses. Compare carriers, book shipments, track deliveries and manage logistics from one intelligent platform.</p>
            {SOCIALS.length > 0 && (
              <div className="mt-5 flex gap-2.5">
                {SOCIALS.map((s) => (
                  <a key={s.label} href={s.href} target="_blank" rel="noreferrer" aria-label={s.label} data-testid={`social-${s.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    className="flex h-9 w-9 items-center justify-center rounded-lg border border-white/10 bg-white/5 text-slate-400 transition-all hover:-translate-y-0.5 hover:border-[#FF9933]/50 hover:text-[#FF9933]">
                    <s.icon className="h-4 w-4" />
                  </a>
                ))}
              </div>
            )}
          </div>
          {FOOTER_COLS.map((col) => (
            <div key={col.title}>
              <h4 className="font-display text-xs font-bold uppercase tracking-widest text-slate-500">{col.title}</h4>
              <ul className="mt-4 space-y-2.5">
                {col.links.map(([label, to]) => (
                  <li key={label}><Link to={to} data-testid={`footer-link-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="text-sm text-slate-400 transition-colors hover:text-shipany-cyan">{label}</Link></li>
                ))}
              </ul>
            </div>
          ))}
          <div>
            <h4 className="font-display text-xs font-bold uppercase tracking-widest text-slate-500">Contact</h4>
            <ul className="mt-4 space-y-2.5">
              <li><Link to="/contact" data-testid="footer-contact-link" className="text-sm font-semibold text-slate-300 transition-colors hover:text-shipany-cyan">ShipAny.in</Link></li>
              {site?.address && (
                <li className="flex items-start gap-2 text-sm text-slate-400" data-testid="footer-address">
                  <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-500" />{site.address}
                </li>
              )}
              {site?.support_phone && (
                <li><a href={`tel:${site.support_phone.replace(/\s/g, "")}`} data-testid="footer-phone-link" className="flex items-center gap-2 text-sm text-slate-400 transition-colors hover:text-shipany-cyan">
                  <Phone className="h-3.5 w-3.5 shrink-0 text-slate-500" />{site.support_phone}</a></li>
              )}
              {waDigits && (
                <li><a href={`https://wa.me/${waDigits}`} target="_blank" rel="noreferrer" data-testid="footer-whatsapp-link" className="flex items-center gap-2 text-sm text-slate-400 transition-colors hover:text-shipany-cyan">
                  <MessageCircle className="h-3.5 w-3.5 shrink-0 text-slate-500" />{site.whatsapp_number}</a></li>
              )}
              <li><a href={`mailto:${contactEmail}`} data-testid="footer-email-link" className="flex items-center gap-2 text-sm text-slate-400 transition-colors hover:text-shipany-cyan">
                <Mail className="h-3.5 w-3.5 shrink-0 text-slate-500" />{contactEmail}</a></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-white/10 py-5 text-center text-xs text-slate-500">
          © {new Date().getFullYear()} ShipAny.in · SHIP ANYWHERE. SHIP SMARTER. · Made in India
        </div>
      </footer>
    </div>
  );
}
